/**
 * TrainingScoreboard Component
 *
 * Displays quality distribution cards for training data
 * Task: F.3 - Scoreboard Cards Component
 *
 * Shows 5 score categories: Excellent, Good, Neutral, Needs Improvement, Bad
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

// Score card configurations
const SCORE_CARDS = [
  {
    key: 'excellent',
    label: 'Excellent',
    scoreValue: '+2',
    color: '#22c55e',
    bgColor: 'rgba(34, 197, 94, 0.1)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
    description: 'Perfect response, highly personalized'
  },
  {
    key: 'good',
    label: 'Good',
    scoreValue: '+1',
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
    description: 'Solid response, minor improvements possible'
  },
  {
    key: 'neutral',
    label: 'Neutral',
    scoreValue: '0',
    color: '#6b7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    borderColor: 'rgba(107, 114, 128, 0.3)',
    description: 'Acceptable but generic'
  },
  {
    key: 'needsImprovement',
    label: 'Needs Work',
    scoreValue: '-1',
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
    description: 'Missing personalization or incomplete'
  },
  {
    key: 'bad',
    label: 'Bad',
    scoreValue: '-2',
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
    description: 'Wrong info, truncated, or harmful'
  }
];

/**
 * Individual Score Card
 */
const ScoreCard = ({ config, count, percentage, trend, isLoading }) => {
  // Determine trend icon
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend > 0) return <TrendingUpIcon sx={{ fontSize: 16, color: '#22c55e' }} />;
    if (trend < 0) return <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />;
    return <TrendingFlatIcon sx={{ fontSize: 16, color: '#6b7280' }} />;
  };

  if (isLoading) {
    return (
      <Card
        sx={{
          backgroundColor: '#292b2f',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          height: '100%'
        }}
      >
        <CardContent>
          <Skeleton variant="text" width="60%" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width="40%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width="30%" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Tooltip title={config.description} placement="top" arrow>
      <Card
        sx={{
          backgroundColor: config.bgColor,
          border: `1px solid ${config.borderColor}`,
          borderRadius: 2,
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${config.borderColor}`
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with label and score value */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: config.color, fontWeight: 600, fontSize: '0.85rem' }}
            >
              {config.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: config.color,
                backgroundColor: 'rgba(0,0,0,0.2)',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 600
              }}
            >
              {config.scoreValue}
            </Typography>
          </Box>

          {/* Count */}
          <Typography
            variant="h4"
            sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}
          >
            {count?.toLocaleString() || 0}
          </Typography>

          {/* Percentage with trend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.6)' }}
            >
              {percentage?.toFixed(1) || 0}%
            </Typography>
            {getTrendIcon()}
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage || 0, 100)}
            sx={{
              mt: 1.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: config.color,
                borderRadius: 2
              }
            }}
          />
        </CardContent>
      </Card>
    </Tooltip>
  );
};

/**
 * Summary Stats Row
 */
const SummaryStats = ({ totals, isLoading }) => {
  const stats = [
    { label: 'Total Conversations', value: totals?.conversations || 0 },
    { label: 'Total Messages', value: totals?.messages || 0 },
    { label: 'Reviewed', value: totals?.reviewed || 0, color: '#22c55e' },
    { label: 'Pending Review', value: totals?.pending || 0, color: '#f59e0b' }
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" width={120} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
      {stats.map((stat) => (
        <Box
          key={stat.label}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            px: 2,
            py: 1,
            minWidth: 100
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
            {stat.label}
          </Typography>
          <Typography variant="h6" sx={{ color: stat.color || 'white', fontWeight: 600 }}>
            {stat.value.toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * TrainingScoreboard Component
 *
 * @param {Object} props
 * @param {Object} props.stats - Training statistics from API
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.showSummary - Show summary stats row (default: true)
 */
const TrainingScoreboard = ({ stats, isLoading = false, showSummary = true }) => {
  const scoreDistribution = stats?.scoreDistribution || {};
  const totals = stats?.totals || {};

  return (
    <Box>
      {/* Summary Stats */}
      {showSummary && (
        <SummaryStats totals={totals} isLoading={isLoading} />
      )}

      {/* Score Cards Grid */}
      <Grid container spacing={2}>
        {SCORE_CARDS.map((config) => {
          const scoreData = scoreDistribution[config.key] || {};
          return (
            <Grid item xs={12} sm={6} md={2.4} key={config.key}>
              <ScoreCard
                config={config}
                count={scoreData.count}
                percentage={scoreData.percentage}
                trend={scoreData.trend}
                isLoading={isLoading}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* No Data Message */}
      {!isLoading && (!stats || Object.keys(scoreDistribution).length === 0) && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'rgba(255,255,255,0.5)'
          }}
        >
          <Typography variant="body2">
            No scoring data available for the selected period.
          </Typography>
          <Typography variant="caption">
            Export conversations and import scores to see quality metrics.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TrainingScoreboard;
export { SCORE_CARDS };
